import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import {auth, db} from '../../firebase'

export type ThemeMode = 'light' | 'dark'

export type ThemeOverrides = Record<string, string>

export type AppThemeDoc = {
  name: string
  description?: string
  overrides: {
    light: ThemeOverrides
    dark: ThemeOverrides
  }
  createdAt?: any
  updatedAt?: any
  createdBy?: string | null
  updatedBy?: string | null
}

export type ActiveThemeSettingsDoc = {
  activeThemeId: string | null
  updatedAt?: any
  updatedBy?: string | null
}

const THEMES_COLLECTION = 'themes'
// Per-user theme preference lives on the user doc at users/{uid}.
// Previously it lived at app_settings/theme (a single global doc) — that
// doc is now orphaned. Each user now gets their own choice, defaulting
// to null (= the classic theme) when unset.
const USERS_COLLECTION = 'users'
const USER_FIELD_ACTIVE_THEME = 'activeThemeId'

// Deprecated — kept only for the 'clear any old session cache' logic
// during the one-time transition. Not referenced by subscribe/set.
const APP_SETTINGS_COLLECTION = 'app_settings'
const APP_THEME_SETTINGS_DOC_ID = 'theme'

function omitUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  const out: Record<string, any> = {}
  Object.keys(obj).forEach((k) => {
    const v = obj[k]
    if (v !== undefined) out[k] = v
  })
  return out as Partial<T>
}

export function themesCollectionRef() {
  return collection(db, THEMES_COLLECTION)
}

/** Firestore reference to the user's settings doc where their per-user
 *  theme preference is stored. Returns null when no user id is available
 *  (caller hasn't authed yet). */
export function userThemeSettingsRef(userId: string) {
  return doc(db, USERS_COLLECTION, userId)
}

export function subscribeToThemes(onChange: (themes: Array<{id: string} & AppThemeDoc>) => void) {
  const q = query(themesCollectionRef(), orderBy('name', 'asc'))
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({id: d.id, ...(d.data() as AppThemeDoc)}))
    onChange(items)
  })
}

/**
 * Subscribe to the CURRENT USER's active theme preference. One doc per
 * user — previously this lived in a single app-wide settings doc, which
 * meant one person's theme switch affected everyone. Now each user
 * picks their own.
 *
 * If the user doc doesn't exist or the field is missing, the callback
 * fires with `null` (= no preference = default theme).
 */
export function subscribeToActiveThemeSettings(
  userId: string,
  onChange: (settings: ActiveThemeSettingsDoc | null) => void
) {
  return onSnapshot(userThemeSettingsRef(userId), (snap) => {
    if (!snap.exists()) return onChange(null)
    const data = snap.data() as Record<string, any>
    const activeThemeId = data?.[USER_FIELD_ACTIVE_THEME] ?? null
    if (activeThemeId === null || typeof activeThemeId === 'string') {
      onChange({
        activeThemeId,
        updatedAt: data?.activeThemeUpdatedAt,
        updatedBy: data?.activeThemeUpdatedBy ?? userId,
      })
      return
    }
    // Defensive: weird data → treat as no preference
    onChange(null)
  })
}

/**
 * Persist the current user's active theme preference to their user doc.
 * Stored as three fields on users/{uid}:
 *   - activeThemeId           (the theme id or null for default)
 *   - activeThemeUpdatedAt    (serverTimestamp — for audit / debugging)
 *   - activeThemeUpdatedBy    (user id, redundant with the doc owner but
 *                              useful if we ever share user docs)
 * Uses setDoc({ merge: true }) so existing user-doc fields (e.g.
 * defaultReplyAccountId, profile prefs) aren't clobbered.
 */
export async function setActiveThemeId(activeThemeId: string | null) {
  const user = auth.currentUser
  if (!user?.uid) {
    // Silently no-op when unauthenticated — the UI shouldn't expose
    // the theme switcher before login, but belt-and-braces.
    return
  }
  await setDoc(
    userThemeSettingsRef(user.uid),
    {
      [USER_FIELD_ACTIVE_THEME]: activeThemeId,
      activeThemeUpdatedAt: serverTimestamp(),
      activeThemeUpdatedBy: user.email ?? user.uid,
    },
    {merge: true},
  )
}

export async function createTheme(input: {name: string; description?: string; base?: Partial<AppThemeDoc>}) {
  const user = auth.currentUser
  const now = serverTimestamp()
  const docData = omitUndefined<AppThemeDoc>({
    name: input.name,
    // Firestore does not allow `undefined` — omit the field entirely when not provided.
    description: input.description,
    overrides: {
      light: input.base?.overrides?.light ?? {},
      dark: input.base?.overrides?.dark ?? {},
    },
    createdAt: now,
    updatedAt: now,
    createdBy: user?.email ?? user?.uid ?? null,
    updatedBy: user?.email ?? user?.uid ?? null,
  } as AppThemeDoc)
  return await addDoc(themesCollectionRef(), docData)
}

export async function updateTheme(
  id: string,
  patch: Partial<Pick<AppThemeDoc, 'name' | 'description' | 'overrides'>>
) {
  const user = auth.currentUser
  // Firestore does not allow `undefined` values in update payloads.
  const payload = omitUndefined({
    ...patch,
    updatedAt: serverTimestamp(),
    updatedBy: user?.email ?? user?.uid ?? null,
  })
  await updateDoc(doc(db, THEMES_COLLECTION, id), payload)
}

export async function deleteTheme(id: string) {
  await deleteDoc(doc(db, THEMES_COLLECTION, id))
}


